<template>
    <div class="layout">
        <div class="layout--column column--left">
            <div class="column--left--content">
                <h2 class="column--left--content--title">
                    Introducing
                    <span class="column--left--content--title--niceai">NiceAI</span>
                </h2>
                <p
                    class="column--left--content--description column--left--content--desktop"
                >
                    NiceAI delivers deeper insights and drives real-world
                    improvements. From revolutionizing feedback collection with
                    smarter, dynamic questions to condensing feedback into
                    actionable insights, NiceAI helps you stay ahead of customer
                    expectations.
                </p>

                <p
                    class="column--left--content--description column--left--content--mobile"
                >
                    NiceAI delivers deeper insights and real-world improvements,
                    with smarter feedback questions and actionable insights.
                </p>

                <p class="column--left--content--image">
                    <img src="/img/login/ai-feature.gif" />
                </p>
                <p>Be one of the first to experience NiceAI.</p>
                <a
                    href="https://www.asknicely.com/product/niceai?utm_source=product&utm_medium=login&utm_campaign=login-page"
                    target="_blank"
                    class="column--left--content--button"
                    >Register your interest</a>
            </div>
        </div>
        <div class="layout--column column--right login">
            <div class="layout--content login-form" @keyup.enter="login">
                <div v-show="error" class="error">{{ error }}</div>
                <CredentialsForm
                    :logo="loginData.logo"
                    class="login-form--credentials-form"
                >
                    <template v-if="mfaEnforce">
                        <div v-if="mfaSuccess">
                            <MFASetupOk
                                :is-loading="isLoading"
                                :codes="recoveryCodes"
                                :from-mfa-setup="true"
                                @onDone="onDone"
                                @onDownload="onDownloadRecoveryCodes"
                            />
                        </div>
                        <div v-else>
                            <MFASetup
                                :email="email"
                                :password="password"
                                :qr-url="mfaQrCode"
                                :mfa-secret="mfaSecret"
                                @mfaUpdate="mfaValidateLogin"
                                @mfaCancel="mfaCancel"
                            />
                        </div>
                    </template>
                    <template v-else-if="!mfaLogin">
                        <template v-if="loginData.emailLoginEnabled">
                            <div class="heading-container">
                                <div class="heading">Sign In</div>
                                <div
                                    v-if="loginData.canSelfRegister"
                                    class="button"
                                >
                                    <a href="/register">
                                        <BaseButton white bordered>Register</BaseButton>
                                    </a>
                                </div>
                            </div>
                            <Input
                                ref="emailRef"
                                v-model="email"
                                title="Email"
                                placeholder="Email"
                                :regex="/.+@.+\..+/"
                                :required="true"
                                err-msg="Valid email is required"
                            />
                            <Input
                                ref="passwordRef"
                                v-model="password"
                                title="Password"
                                placeholder="Password"
                                type="password"
                                :required="true"
                                err-msg="Invalid Password"
                            />
                            <GrecaptchaBox
                                v-if="loginData.hasRecaptcha"
                                ref="grecaptchaRef"
                                :sitekey="loginData.recaptcha"
                            />
                            <BaseButton
                                class="sign-in-button sign-in-button--purple-bg"
                                new-primary
                                full
                                large
                                :click="login"
                                >Sign In</BaseButton>
                            <div class="forgot">
                                <a href="/password">Forgot Password</a>
                            </div>
                        </template>

                        <template
                            v-if="
                                loginData.emailLoginEnabled &&
                                loginData.hasOauthLogins
                            "
                        >
                            <div class="or">
                                <div class="line"></div>
                                <div class="or-text">or</div>
                                <div class="line"></div>
                            </div>
                        </template>

                        <BaseButton
                            v-if="loginData.googleUrl"
                            :click="() => redirectTo(loginData.googleUrl)"
                            class="sign-in-button sign-in-button--outline"
                            primary
                            outline
                            full
                            large
                        >
                            <img
                                class="oauth-icon"
                                src="/img/login/google-oauth-icon.svg"
                            />
                            Sign in with Google
                        </BaseButton>
                        <BaseButton
                            v-if="loginData.oktaUrl"
                            :click="() => redirectTo(loginData.oktaUrl)"
                            class="sign-in-button sign-in-button"
                            primary
                            outline
                            full
                            large
                        >
                            <img
                                class="oauth-icon"
                                src="/img/login/okta-oauth-icon.png"
                            />
                            Sign in with Okta
                        </BaseButton>
                        <BaseButton
                            v-if="loginData.microsoftUrl"
                            :click="() => redirectTo(loginData.microsoftUrl)"
                            class="sign-in-button sign-in-button--outline"
                            primary
                            outline
                            full
                            large
                        >
                            <img
                                class="oauth-icon"
                                src="/img/login/microsoft-oauth-icon.png"
                            />
                            Sign in with Microsoft 365
                        </BaseButton>
                        <BaseButton
                            v-if="loginData.samlSsoUrl"
                            :click="() => redirectTo(loginData.samlSsoUrl)"
                            class="sign-in-button sign-in-button--outline"
                            primary
                            outline
                            full
                            large
                            >{{ loginData.samlSsoName }}</BaseButton>
                    </template>
                    <template v-else>
                        <!-- If user hs no more recovery codes, show new recovery codes if they were generated -->
                        <template v-if="mfaSuccess && recoveryCodes">
                            <MFASetupOk
                                :is-loading="isLoading"
                                :codes="recoveryCodes"
                                :from-mfa-setup="false"
                                @onDone="onDone"
                                @onDownload="onDownloadRecoveryCodes"
                            />
                        </template>
                        <template v-else-if="isMfaRecoveryPage">
                            <MFAVerifyRecoveryCode
                                @onBack="mfaCancel"
                                @onSubmit="onVerifyRecoveryCode"
                            />
                        </template>
                        <template v-else>
                            <div class="heading">Enter Multi-Factor Code</div>
                            <Input
                                ref="mfaRef"
                                v-model="mfa"
                                title="Multi-Factor Authentication Code"
                                type="number"
                                placeholder="6 digit MFA code"
                                :required="true"
                                :err-msg="'Code cannot be empty'"
                            />
                            <Checkbox
                                class="remember-device-checkbox"
                                :checked="mfaRememberDevice"
                                @change="onChangeRememberDevice"
                                >Remember this device for 30 days</Checkbox>
                            <BaseButton
                                class="sign-in-button"
                                new-primary
                                full
                                large
                                :click="login"
                                >Sign In</BaseButton>
                            <div class="forgot">
                                <a href="/password">Forgot Password</a>
                            </div>
                            <div class="lost-access-legend">
                                <a @click="onLostAuthenticatorLink">Lost Access To My Authenticator</a>
                            </div>
                        </template>
                    </template>

                    <div class="ask-logo">
                        <a href="https://www.asknicely.com" target="_blank">
                            <img
                                src="/img/asknicely_logo_grey.svg"
                                alt="AskNicely"
                            />
                        </a>
                    </div>
                </CredentialsForm>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, onMounted, nextTick } from 'vue'
import CredentialsForm from '@/components/CredentialsForm.vue'
import Input from '@/components/Input.vue'
import BaseButton from '@/components/BaseButton.vue'
import { LoginData } from '@/pages/login/entities/login'
import { loginUser } from '@/api/login'
import GrecaptchaBox from '@/components/GrecaptchaBox.vue'
import Checkbox from '@/components/Rain/Inputs/Checkbox.vue'
import MFASetup from '@/components/Settings/MFASetup.vue'
import MFASetupOk from '@/pages/login/MFASetupOk.vue'
import MFAVerifyRecoveryCode from '@/pages/login/MFAVerifyRecoveryCode.vue'

const DISABLED_SUBDOMAINS: string[] = ['asknicely']

const email = ref('')
const password = ref('')
const mfa = ref('')
const mfaRememberDevice = ref(false)
const loginData = ref<LoginData | undefined>()
const error = ref('')
const mfaLogin = ref(false)
const mfaEnforce = ref(false)
const mfaQrCode = ref('')
const mfaSecret = ref('')
const mfaSuccess = ref(false)
const isMfaRecoveryPage = ref(false)
const recoveryCodes = ref<string[]>([])
const recoveryCode = ref('')
const isLoading = ref(true)
const redirectUrl = ref('')

const emailRef = ref<typeof Input | null>(null)
const passwordRef = ref<typeof Input | null>(null)
const mfaRef = ref<HTMLInputElement | null>(null)
const grecaptchaRef = ref<typeof GrecaptchaBox | null>(null)

onBeforeMount(async () => {
    const dataEl = document.getElementById('login-data')
    const dataJson = dataEl && dataEl.getAttribute('data-json')
    const data: LoginData = dataJson && JSON.parse(dataJson)

    loginData.value = data
    error.value = data.error
})

const login = async () => {
    error.value = ''
    let recaptchaResponse = ''

    if (loginData.value?.hasRecaptcha) {
        recaptchaResponse = grecaptchaRef.value?.getResponse() || ''
        if (recaptchaResponse.length < 1) {
            error.value = 'Please complete reCAPTCHA challenge'
            return
        }
    }

    if (!mfaLogin.value && !mfaEnforce.value) {
        emailRef.value!.hasFocused = true
        passwordRef.value!.hasFocused = true
    }

    if (password.value === '' || !email.value.match(/.+@.+\..+/)) {
        return
    }

    if (mfaLogin.value && mfa.value === '') {
        return
    }

    try {
        const { data } = await loginUser(
            email.value,
            password.value,
            mfaLogin.value || mfaEnforce.value ? mfa.value : undefined,
            loginData.value?.hasRecaptcha ? recaptchaResponse : undefined,
            mfaLogin.value ? mfaRememberDevice.value : undefined,
            recoveryCode.value
        )
        if (data.mfa) {
            mfaLogin.value = true
            nextTick(() => {
                mfaRef.value?.focus()
            })
        } else if (data.mfaEnforce) {
            mfaEnforce.value = true
            mfaQrCode.value = data.codeUrl
            mfaSecret.value = data.mfaSecret
        } else if (data.recoveryCodes) {
            recoveryCodes.value = data.recoveryCodes
            mfaSuccess.value = true
            isLoading.value = false
            redirectUrl.value = data.redirect
        } else {
            redirectTo(data.redirect)
        }
    } catch (e: any) {
        error.value = e.response.data.error
    }
}

const verifyRecoveryCode = async () => {
    try {
        error.value = ''
        let recaptchaResponse = ''

        const { data } = await loginUser(
            email.value,
            password.value,
            mfaLogin.value || mfaEnforce.value ? mfa.value : undefined,
            loginData.value?.hasRecaptcha ? recaptchaResponse : undefined,
            mfaLogin.value ? mfaRememberDevice.value : undefined,
            recoveryCode.value
        )

        if (data.recoveryCodes) {
            recoveryCodes.value = data.recoveryCodes
            mfaSuccess.value = true
            isLoading.value = false
            redirectUrl.value = data.redirect
        } else {
            redirectTo(data.redirect)
        }
    } catch (e: any) {
        error.value = e.response.data.error
    }
}

const mfaValidateLogin = (mfaCode: string) => {
    if (!mfaCode || mfaCode.length !== 6) {
        error.value = 'Please enter your 6 digit MFA code'
        return
    }
    mfa.value = mfaCode
    login()
}

const onDownloadRecoveryCodes = () => {
    const dataAsString = recoveryCodes.value.join('\n')
    const blob = new Blob([dataAsString], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = 'recovery-codes.txt'
    document.body.appendChild(link)

    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
}

onMounted(() => {
    window.addEventListener('keydown', (e) => {
        const isCommandG = (e.metaKey || e.ctrlKey) && e.key === 'g'
        const subDomain = window.location.host.split('.')[0]
        const notDisabled = DISABLED_SUBDOMAINS.indexOf(subDomain) === -1
        if (notDisabled && isCommandG && loginData.value?.enabledGLogin) {
            e.preventDefault()
            redirectTo(loginData.value.asknicelyLoginUrl)
        }
    })
})

const mfaCancel = () => {
    error.value = ''
    mfaEnforce.value = false
    mfaQrCode.value = ''
    mfaSecret.value = ''
    isMfaRecoveryPage.value = false
    recoveryCode.value = ''
}

const redirectTo = (link: string) => {
    return location.replace(link)
}

const onChangeRememberDevice = (value: boolean): void => {
    mfaRememberDevice.value = value
}

const onLostAuthenticatorLink = (): void => {
    isMfaRecoveryPage.value = true
}

const onVerifyRecoveryCode = (code: string) => {
    if (!code) {
        error.value = 'Recovery code required.'
        return
    }
    recoveryCode.value = code
    verifyRecoveryCode()
}

const onDone = () => {
    redirectTo(redirectUrl.value)
}
</script>

<style lang="less" scoped>
@import '../../styles/palette';
@import '@/styles/prime-vue/variables.less';

/* Layout */
.layout {
    display: flex;
    height: 100%;
    width: 100%;
    background-color: #f1eee9;

    &--column {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }
}

.column {
    &--left {
        color: @white;
        background-color: @bgDarkGradientStart;

        &--content {
            width: 500px;

            &--title {
                display: flex;
                font-size: 22px;
                font-weight: 500;
                text-transform: uppercase;
                align-items: center;
                margin: 0;

                &--niceai {
                    display: block;
                    width: 274px;
                    height: 75px;
                    background-image: url('data:image/svg+xml;base64,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');
                    text-indent: -9999px;
                    overflow: hidden;
                    background-size: contain; /* This will make the image fit within the element */
                    background-repeat: no-repeat; /* Prevent the image from repeating */
                    background-position: center; /* Center the image within the element */
                }
            }

            &--image img {
                width: 500px;
                border-radius: @radius-lg;
                margin-top: 10px;
            }

            &--desktop {
                display: block;
            }

            &--mobile {
                display: none;
            }

            p,
            &--description {
                font-size: 15px;
                line-height: 22px;
                letter-spacing: 0.1px;
                text-align: left;
            }

            &--button {
                display: inline-block;
                color: white;
                background-color: @indigo;
                border-radius: @radius-xs;
                padding: 10px @spacing-xl;
                margin-top: @spacing-xs;

                &:hover {
                    text-decoration: none;
                    background-color: @bgDarkGradientStop;
                }
            }
        }
    }

    &--right {
        background-color: @white;
    }
}

/* Media query for screens smaller than 768px */
@media screen and (max-width: 767px) {
    .layout {
        flex-direction: column;

        &--column {
            flex: none;
            padding: @spacing-6xl 0;
        }
    }

    .column {
        &--left {
            color: @white;
            background-color: @bgDarkGradientStart;

            &--content {
                width: 350px;

                &--title {
                    display: flex;
                    font-size: 22px;
                    font-weight: 500;
                    text-transform: uppercase;
                    align-items: center;
                    margin-bottom: 15px;

                    &--niceai {
                        display: block;
                        width: 207px;
                        height: 58px;
                        background-image: url('data:image/svg+xml;base64,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');
                        text-indent: -9999px;
                        overflow: hidden;
                        background-size: contain; /* This will make the image fit within the element */
                        background-repeat: no-repeat; /* Prevent the image from repeating */
                        background-position: center; /* Center the image within the element */
                    }
                }

                &--image {
                    display: none;
                }

                &--desktop {
                    display: none;
                }

                &--mobile {
                    display: block;
                }
            }
        }
    }
}

.login {
    .login-form {
        margin: 0 auto;

        &--credentials-form {
            padding: 0 !important;
            width: 350px;
        }

        .input-container {
            :deep(::-webkit-outer-spin-button),
            :deep(::-webkit-inner-spin-button) {
                display: none;
                -webkit-appearance: none;
                margin: 0;
            }

            :deep(input[type='number']) {
                -moz-appearance: textfield; /* Firefox */
            }
        }

        .remember-device-checkbox {
            margin-top: 14px;
        }

        .sign-in-button {
            margin-top: 14px;

            &--purple-bg {
                color: @white;
                background-color: @bgDarkGradientStart;
            }

            &--outline {
                border-color: #d0d5dd;
                color: #344054;
            }
        }

        .ask-logo {
            margin-top: 22px;
            text-align: center;

            img {
                height: 20px;
            }
        }

        .heading {
            margin-top: 22px;
            font-size: 20px;
            font-weight: 500;
        }

        .heading-container {
            display: flex;
            align-items: center;

            .heading {
                flex: 1;
                margin-top: 0;
            }
        }

        .error {
            color: @red;
            font-size: 12px;
            margin-top: 8px;
            background-color: #ffebeb;
            padding: 12px 10px;
            text-align: center;
            border-radius: 5px;
            margin-bottom: 20px;
        }

        .forgot,
        .lost-access-legend {
            text-align: right;
            margin-top: 10px;

            a {
                font-size: 13px;
                color: @grey40;
                text-decoration: none;
            }
        }

        .or {
            display: flex;
            text-align: center;
            color: @grey40;
            padding-top: 14px;

            .line {
                width: 160px;
                border-bottom: 1px solid @grey40;
                position: relative;
                top: -8px;
            }

            .or-text {
                padding: 0 8px;
            }
        }

        .oauth-icon {
            position: relative;
            top: 3px;
            left: -2px;
        }
    }
}
.lost-access-legend {
    line-height: 18px;
    cursor: pointer;
}
</style>
